import { createContext, useContext, useState, type ReactNode } from 'react';
import { useAuth } from 'auth-sso-kit/client/index.js';
import { setRolePreview } from '../api/client';
import type { Role } from '../types';

// Dérive les rôles MÉTIER depuis auth-sso-kit (is_admin + rôles dynamiques).
// Une seule maison pour la logique de rôles côté client — les gardes serveur
// restent la vraie barrière (middleware/roleAuth.ts).
const ROLES_METIER: Role[] = ['intervenant', 'responsable', 'secretariat', 'rh', 'planification'];

interface RoleContextValue {
  roles: Role[];
  isAdmin: boolean;
  aRole: (...r: Role[]) => boolean;   // admin passe toujours
  peutGerer: boolean;                 // responsable | secretariat | rh | admin
  // Aperçu intervenant·e : un admin peut s'auto-restreindre pour prévisualiser
  // fidèlement l'expérience d'un·e utilisateur·rice lambda (serveur ET client).
  // État en mémoire uniquement — un F5 repasse automatiquement en vue réelle.
  apercuActif: boolean;
  estAdminReel: boolean;
  activerApercu: () => void;
  desactiverApercu: () => void;
}

const RoleContext = createContext<RoleContextValue>({
  roles: [], isAdmin: false, aRole: () => false, peutGerer: false,
  apercuActif: false, estAdminReel: false, activerApercu: () => {}, desactiverApercu: () => {},
});

export function RoleProvider({ children }: { children: ReactNode }) {
  const { user, isAdmin: isAdminReel, roles: kitRoles } = useAuth();
  const [apercuActif, setApercuActif] = useState(false);

  const rolesReels: Role[] = (kitRoles ?? []).filter((r): r is Role => (ROLES_METIER as string[]).includes(r));
  if (user && isAdminReel) rolesReels.push('admin');
  if (user && rolesReels.length === 0) rolesReels.push('intervenant');

  const roles: Role[] = apercuActif ? ['intervenant'] : rolesReels;
  const isAdmin = apercuActif ? false : isAdminReel;

  function activerApercu() { setRolePreview(true); setApercuActif(true); }
  function desactiverApercu() { setRolePreview(false); setApercuActif(false); }

  const aRole = (...r: Role[]) => isAdmin || roles.some((x) => r.includes(x));

  return (
    <RoleContext.Provider value={{
      roles, isAdmin, aRole, peutGerer: aRole('responsable', 'secretariat', 'rh'),
      apercuActif, estAdminReel: isAdminReel, activerApercu, desactiverApercu,
    }}>
      {children}
    </RoleContext.Provider>
  );
}

export function useRole() {
  return useContext(RoleContext);
}
